import { useAuth0 } from '@auth0/auth0-react';
import { LicenseInfo } from '@mui/x-license';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { createContextFactory } from '@tempo-io/feature-flag-context';
import { setFroalaLicenseKey } from '@tempo-io/fim3-sui-shared';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { GlobalErrorBoundary } from '@/components/common/error/GlobalErrorBoundary';
import { FeatureFlagDevTools } from '@/components/dev-tools/FeatureFlagDevTools';
import { FullSizeSpinner } from '@/components/ui/FullSizeSpinner';
import { useFlagOverrides } from '@/lib/hooks/feature-flags/useFlagsWithOverrides';
import { useDatadogUserTracking } from '@/lib/hooks/monitoring/useDatadogUserTracking';
import { useLaunchDarklyDatadogIntegration } from '@/lib/hooks/monitoring/useLaunchDarklyDatadogIntegration';
import { AuthProvider } from '@/lib/providers/auth-provider/authProvider';
// biome-ignore lint/style/noRestrictedImports: the QueryClientProvider needs the singleton; all other code must use useQueryClient()
import { queryClient } from '@/lib/queryClient';
import { getEnv } from '@/lib/utils/common/getEnv';
import { TRPCProvider } from './lib/trpc/trpc';
import { router } from './router';

export const ldContextFactory = createContextFactory({ namespace: getEnv().namespace });

function RouterProviderWithContext() {
  const auth = useAuth0();
  const flagOverrides = useFlagOverrides();
  const ldClient = useLDClient();
  useDatadogUserTracking();
  useLaunchDarklyDatadogIntegration();

  if (!ldClient) {
    return (
      <div className="h-screen w-screen">
        <FullSizeSpinner />
      </div>
    );
  }

  return <RouterProvider router={router} context={{ auth, flagOverrides, ldClient }} />;
}

function App() {
  const env = getEnv();
  LicenseInfo.setLicenseKey(env.muiProLicenseId || '');
  setFroalaLicenseKey(env.froalaLicenseId || '');
  return (
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TRPCProvider>
            <RouterProviderWithContext />
            <FeatureFlagDevTools />
          </TRPCProvider>
        </AuthProvider>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  );
}

export { App };
